/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * The ChartInsightsQueryParams is used to hide the details of creating the parameters needed for NLT
 * both for addition to the data query and for the 'dictionary' section of the chart-insights query.
 */
class ChartInsightsQueryParams {
	constructor(layerId, version) {
		this.datasetId = layerId;
		this.version = version;

		this.dataItems = [];
		this.dataItemLabels = [];
		this.roles = [];
		this.dataItemsTypes = [];
		this.moduleAssetId = null;
		this.isShapedModule = false;
		this.tempModuleJSON = null;
	}

	/**
	 * Add a new parameter to the set of parameters from the specified slot and dataItem sorting response items from all others.
	 * @param slot - the slot (needed to determine the role and, when stacked, to supply the itemId for dictionary mode)
	 * @param dataItem - the dataItem to add
	 * @param isDictionary - when true, a nested slot will be added with the id of the slot...when false,
	 *						the ids will always be the itemId of the dataItem.
	 * @param dataSource - the dataSouce object will check if the source used is OLAP or not.
	 */
	addParam(slot, dataItem, isDictionary, role, dataSource, dataSourceMoserApi = null) {
		role = role || slot.getDefinition().getRole();
		let isOlapPackage = false;
		if(dataSource && dataSource.isOlapPackage){
			isOlapPackage = dataSource.isOlapPackage();
		}
		const isShapedModule = !!dataSourceMoserApi &&  !!dataSourceMoserApi.getShapingId();
		const tempModule = dataSourceMoserApi && dataSourceMoserApi.getTemporarySessionModuleJSON();
		const dataItemId = (isDictionary && slot.isStacked()) ? slot.getDefinition().getId() : dataItem.getId();
		if (!isDictionary && dataItem.getMetadataColumn() && dataItem.getMetadataColumn().isOlapColumn(isOlapPackage)) {
			//olap items can be sent to chart insights but do not include them in calculations.
			return false;
		}

		//Calculations should only be built on non-binned response items.
		//Dictionaries for the chartInsights request should include all roles.
		if (role==='response') {
			if (isDictionary || !dataItem.getBinning()) {
				this.dataItems.push(dataItemId);
				this.dataItemLabels.push(dataItem.getLabel());
				this.dataItemsTypes.push(this._getDataItemType(dataItem));
				this.moduleAssetId = dataSource && dataSource.getAssetId();
				this.roles.push(role);
				return true;
			}
		} else if (isDictionary) {
			this.isShapedModule = isShapedModule;
			this.dataItems.push(dataItemId);
			this.dataItemLabels.push(dataItem.getLabel());
			this.dataItemsTypes.push(this._getDataItemType(dataItem));
			this.moduleAssetId = dataSource && dataSource.getAssetId();
			this.roles.push(role);
			this.tempModuleJSON = tempModule;
			return true;
		}
		return false;
	}

	/**
	 * Add a calculation to the calculation array in the form expected by Live Widget for adding calculations to the DSS query.
	 * NOTE: This function is not used in the chart-insights query to predict.
	 * @param {Object} slot - the slot (needed to determine the role and, when stacked, to supply the itemId for dictionary mode)
	 * @param {Object} dataItem - the dataItem to add
	 * @param {Object} dataSource, the dataSource object, needed to determine if the soure is an OLAP package  or not.
	 * @returns {Object} the new calculation in the form expected by Live Widget for adding to the query.
	 */
	addDSSRowCountCalculation(slot, dataItem, dataSource) {
		if (this.addParam(slot, dataItem, false, 'response', dataSource)) {
			return this.getParams(['rowCount']);
		}
		return null;
	}

	hasParams() {
		return (this.dataItems.length > 0 && this.roles.length > 0) || false;
	}

	_getDataItemType(dataItem) {
		const dateTypes = ['datetime', 'time', 'date'];
		const metadata = dataItem.getMetadataColumn();
		const taxonomyList = metadata && metadata.getTaxonomyList();
		const itemDataType = dataItem.getType();
		if (dateTypes.indexOf(itemDataType) > -1 || taxonomyList && taxonomyList.length && taxonomyList[0].getClass() === 'cTime') {
			return 'datetime';
		} else if (itemDataType === 'attribute') {
			return 'categorical';
		} else if (itemDataType === 'fact') {
			return dataItem.getBinning() ? 'categorical' : 'continuous';
		}
		return '';
	}

	/**
	 * get the parameters needed both for the DSS query (to append calculations)
	 * AND for the chart Insights dictionary (for the chart insights request).
	 * @param {String[]} [reqStats] the calculations to add (by default, these are rowcount and sum of squares)
	 * @returns {Object} the new calculation in the form expected by both Live Widget for adding to the DSS query
	 *										AND for the dictionary section of the chart-insights query
	 */
	getParams(reqStats) {
		const params = {
			shapedModule: this.isShapedModule,
			datasetId: this.datasetId,
			dataItems: this.dataItems,
			dataItemsTypes: this.dataItemsTypes,
			roles: this.roles,
			moduleAssetId: this.moduleAssetId,
			reqStats: reqStats || [ 'rowCount', 'sumSqr' ],
			type: 'chartInsights'
		};

		if (this.isShapedModule) {
			params.tempModule = JSON.stringify(this.tempModuleJSON);
		}

		if (this.version === '2') {
			params.dataItemLabels = this.dataItemLabels;
		}

		return params;
	}
}

export default ChartInsightsQueryParams;



// WEBPACK FOOTER //
// ./src/js/features/insights/ChartInsightsQueryParams.js